import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * Common currencies with symbols. First column is the ISO-4217 code, which is
 * also stored / displayed as the "unit" next to amounts.
 */
export const CURRENCIES = [
  { code: "OMR", symbol: "ر.ع.", name_ar: "ريال عماني", name_en: "Omani Rial" },
  { code: "AED", symbol: "د.إ", name_ar: "درهم إماراتي", name_en: "UAE Dirham" },
  { code: "SAR", symbol: "ر.س", name_ar: "ريال سعودي", name_en: "Saudi Riyal" },
  { code: "KWD", symbol: "د.ك", name_ar: "دينار كويتي", name_en: "Kuwaiti Dinar" },
  { code: "BHD", symbol: "د.ب", name_ar: "دينار بحريني", name_en: "Bahraini Dinar" },
  { code: "QAR", symbol: "ر.ق", name_ar: "ريال قطري", name_en: "Qatari Riyal" },
  { code: "USD", symbol: "$", name_ar: "دولار أمريكي", name_en: "US Dollar" },
  { code: "EUR", symbol: "€", name_ar: "يورو", name_en: "Euro" },
  { code: "GBP", symbol: "£", name_ar: "جنيه إسترليني", name_en: "British Pound" },
  { code: "EGP", symbol: "ج.م", name_ar: "جنيه مصري", name_en: "Egyptian Pound" },
  { code: "JOD", symbol: "د.أ", name_ar: "دينار أردني", name_en: "Jordanian Dinar" },
  { code: "TRY", symbol: "₺", name_ar: "ليرة تركية", name_en: "Turkish Lira" },
  { code: "INR", symbol: "₹", name_ar: "روبية هندية", name_en: "Indian Rupee" },
  { code: "PKR", symbol: "₨", name_ar: "روبية باكستانية", name_en: "Pakistani Rupee" },
  { code: "CNY", symbol: "¥", name_ar: "يوان صيني", name_en: "Chinese Yuan" },
  { code: "JPY", symbol: "¥", name_ar: "ين ياباني", name_en: "Japanese Yen" },
  { code: "CAD", symbol: "C$", name_ar: "دولار كندي", name_en: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name_ar: "دولار أسترالي", name_en: "Australian Dollar" },
  { code: "MAD", symbol: "د.م.", name_ar: "درهم مغربي", name_en: "Moroccan Dirham" },
  { code: "IQD", symbol: "د.ع", name_ar: "دينار عراقي", name_en: "Iraqi Dinar" },
];

const STORAGE_KEY = "aqar_currency";
const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [code, setCodeState] = useState(() => {
    if (typeof window === "undefined") return "OMR";
    return localStorage.getItem(STORAGE_KEY) || "OMR";
  });

  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem(STORAGE_KEY, code);
  }, [code]);

  const value = useMemo(() => {
    const current = CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];
    const format = (amount, opts = {}) => {
      const n = Number(amount) || 0;
      const decimals = opts.decimals ?? 2;
      const fixed = n.toFixed(decimals);
      return `${fixed} ${current.code}`;
    };
    return {
      code,
      setCode: setCodeState,
      currency: current,
      symbol: current.symbol,
      format,
      list: CURRENCIES,
    };
  }, [code]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
